import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import ProfileCircle from '../common/profile/ProfileCircle';
import Permissions from '../auth/Permissions';
import { getIdMap } from '../helpers/helpers';
import { getAllDayValues } from '../helpers/time';
import { getBackgroundColor, getTextColor } from './scheduleHelpers';
import { formatProjectLabelFromCompanySettings } from '../projects/projectHelpers';

const compactTime = (millis) => {
  const dt = DateTime.fromMillis(millis);
  return dt.minute ? dt.toFormat('h:mm a') : dt.toFormat('h a');
};

const formatDuration = (startTime, endTime) => {
  const minutes = Math.max(0, Math.round((endTime - startTime) / 60000));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  if (mins) return `${mins}m`;
  return '';
};

const initialsFor = (name = '') => {
  const parts = name.split(' ').filter(Boolean);
  if (!parts.length) return '';
  return `${parts[0][0] || ''}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase();
};

function PinIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function ScheduleListShift({ shift, onSelect }) {
  const stateUsers = useSelector((state) => state.users.users);
  const projects = useSelector((state) => state.projects.projects);
  const phases = useSelector((state) => state.costcodes.phases);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const categories = useSelector((state) => state.schedule.categories);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const userMap = useMemo(() => getIdMap(stateUsers), [stateUsers]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const phaseMap = useMemo(() => getIdMap(phases), [phases]);
  const categoryMap = useMemo(() => getIdMap(categories || []), [categories]);

  const {
    title,
    startTime,
    endTime,
    users: shiftUserIds = [],
    color,
    isEvent,
    isDraft,
    projectId,
    phaseId,
    costcodeId,
    shiftCategoryId,
    categoryId,
    timezone = DateTime.local().zoneName,
  } = shift;

  const isMyShift = shiftUserIds.includes(Permissions.id);
  const backgroundColor = getBackgroundColor(
    shiftUserIds,
    userMap,
    color,
    isMyShift,
    isEvent,
    isDraft,
  );
  const accent = ['#fff', '#ffffff', '#ffffffff'].includes((backgroundColor || '').toLowerCase())
    ? '#D4D4D8'
    : backgroundColor;

  const { isAllDay } = getAllDayValues(
    DateTime.fromMillis(startTime, { zone: timezone }),
    DateTime.fromMillis(endTime, { zone: timezone }),
  );
  const timeLabel = isAllDay
    ? 'All day'
    : `${compactTime(startTime)} – ${compactTime(endTime)}`;
  const duration = isAllDay ? '' : formatDuration(startTime, endTime);

  const project = projectMap[projectId];
  const projectName = project
    ? formatProjectLabelFromCompanySettings({
      name: project.name,
      number: project.number,
      settings,
    })
    : '';
  const phaseName = phaseMap[phaseId]?.name;
  const costcode = costcodeIdMap?.[costcodeId];
  const costcodeLabel = costcode
    ? [costcode.code, costcode.name].filter(Boolean).join(' - ')
    : '';
  const category = categoryMap[shiftCategoryId || categoryId] || {};
  const assigned = shiftUserIds
    .map((userId) => userMap[userId])
    .filter((user) => user && user.name);

  return (
    <button
      type="button"
      className="schedule-list-shift"
      style={{ borderLeftColor: accent }}
      onClick={() => onSelect(shift)}
    >
      <div className="schedule-list-shift-top">
      <div className="schedule-list-shift-main">
        <div className="schedule-list-shift-time">{timeLabel}</div>
        <div className="schedule-list-shift-title">{title}</div>
        <div className="schedule-list-shift-meta">
          {!!projectName && (
            <span className="schedule-list-shift-meta-item">
              <PinIcon />
              {projectName}
            </span>
          )}
          {!!phaseName && <span className="schedule-list-shift-meta-item">{phaseName}</span>}
          {!!costcodeLabel && <span className="schedule-list-shift-meta-item">{costcodeLabel}</span>}
          {!!category.name && (
            <span className="schedule-list-shift-meta-item">
              <span
                className="schedule-shift-hover-card-dot"
                style={{ backgroundColor: category.color || '#A1A1AA' }}
              />
              {category.name}
            </span>
          )}
        </div>
      </div>
      <div className="schedule-list-shift-side">
        {!!isDraft && <span className="schedule-list-shift-draft">Draft</span>}
        {!!duration && <span className="schedule-list-shift-duration">{duration}</span>}
      </div>
      </div>
      <div className="schedule-list-shift-people">
          {assigned.length === 0 && (
            <>
              <span className="schedule-list-shift-unassigned">?</span>
              <span>Unassigned</span>
            </>
          )}
          {assigned.length === 1 && (
            <>
              <ProfileCircle
                radius={12}
                fontSize={9}
                initials={initialsFor(assigned[0].name)}
                style={assigned[0].scheduleColor ? { backgroundColor: assigned[0].scheduleColor } : {}}
                accentColor={assigned[0].scheduleColor ? getTextColor(assigned[0].scheduleColor) : undefined}
              />
              <span>
                <span className="schedule-list-shift-person-name">{assigned[0].name}</span>
                {!!assigned[0].position && (
                  <span className="schedule-list-shift-person-role">{assigned[0].position}</span>
                )}
              </span>
            </>
          )}
          {assigned.length > 1 && (
            <>
              <span className="schedule-list-shift-avatars">
                {assigned.map((user, idx) => (
                  <ProfileCircle
                    key={user.id}
                    radius={10}
                    fontSize={8}
                    initials={initialsFor(user.name)}
                    style={{
                      backgroundColor: user.scheduleColor,
                      zIndex: assigned.length - idx,
                    }}
                    accentColor={user.scheduleColor ? getTextColor(user.scheduleColor) : undefined}
                  />
                ))}
              </span>
              <span>
                <span className="schedule-list-shift-person-name">{assigned[0].name}</span>
                <span className="schedule-list-shift-person-role">
                  <span className="schedule-list-shift-person-extra">{`+${assigned.length - 1} more`}</span>
                  <span className="schedule-list-shift-person-extra">{` · ${assigned.length} crew`}</span>
                </span>
              </span>
            </>
          )}
      </div>
    </button>
  );
}

ScheduleListShift.propTypes = {
  shift: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default ScheduleListShift;
